import { useAppSelector } from 'hooks/useRedux';
import { ConfigurableHyperLinkSelector } from 'state/hyperlink/hyperlink.selectors';
import './ProviderInstruction.scss';
import prescriber from '../icons/prescriber.svg';
import patient from '../icons/patient.svg';

import {
  faArrowRight,
  faEnvelopeOpenText,
  faUserFriends,
  faUserLock,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { Button, Col, Container, Row } from 'react-bootstrap';
import Card from '../components/Card';
import { FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HighlightSection from 'components/HightlightSection/HighlightSection';
import WorkflowNavigationButton from 'components/Buttons/WorkflowNavigationButton/WorkflowNavigationButton';

interface ProviderInstructionProps {
  handleGetStarted?: (e: FormEvent) => void;
}

const ProviderInstruction = ({ handleGetStarted }: ProviderInstructionProps) => {
  const hyperlinks = useAppSelector(ConfigurableHyperLinkSelector);

  return (
    <div className="d-flex lh-1">
      <Container fluid>
        <Row>
          <HighlightSection coloredLineClassName="colored-line-provider-instructions">
            <div className="w-100 px-2 px-sm-3">
              <div className="mt-4">
                <h2 className="fw-bold my-auto h2">
                  Welcome to the XYWAV and XYREM REMS ePrescription Registration Process
                </h2>
              </div>
              <hr></hr>
              <Row className="justify-content-between pt-2 pb-4">
                <Col
                  md={6}
                  lg={7}
                  xl={8}
                >
                  <div>
                    In order to electronically prescribe XYWAV and XYREM securely, <b>first</b> you
                    and your patient <b>must</b> be enrolled in the XYWAV and XYREM REMS.
                  </div>
                  <br></br>
                  <div>
                    <p
                      style={{ fontSize: '13px' }}
                      className="lh-1 text-grey py-1"
                    >
                      By clicking the link to enroll yourself or your patient, you will leave the
                      EPCS platform and need to reopen this page in order to continue the onboarding
                      process.
                    </p>
                  </div>
                  <div className="pb-1">
                    <b>Next</b>, you must complete a DEA-compliant onboarding process, known as the
                    Electronic Prescribing for Controlled Substances (EPCS).
                  </div>
                </Col>
                <Col
                  md={6}
                  lg={5}
                  xl={4}
                  className="d-flex flex-column align-items-end pt-md-0 pt-3"
                >
                  <Button
                    id="complete-rems-cert-anchor-instruction"
                    href={hyperlinks['configurable_hyperlink_1'] || '/'}
                    target="_blank"
                    variant="danger"
                    size="sm"
                    className="d-flex d-inline-flex align-items-center gap-2 px-2 px-sm-4 rounded-pill mb-3 w-100 fw-bold prescriber-instructions-btn"
                  >
                    <img src={prescriber} />
                    Prescriber REMS Certification
                    <div className="ms-auto d-flex ">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        color="white"
                        size="xs"
                      />
                    </div>
                  </Button>

                  <Button
                    id="complete-rems-enrollment-anchor-instruction"
                    href={hyperlinks['configurable_hyperlink_2'] || '/'}
                    target="_blank"
                    variant="danger"
                    size="sm"
                    className="d-flex d-inline-flex align-items-center gap-2 px-2 px-sm-4 rounded-pill w-100 fw-bold prescriber-instructions-btn"
                  >
                    <img src={patient} />
                    Patient REMS Enrollment
                    <div className="ms-auto d-flex ">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        color="white"
                        size="xs"
                      />
                    </div>
                  </Button>
                </Col>
              </Row>
            </div>
          </HighlightSection>

          <div className="w-100">
            <Container className="mb-3">
              <div className="mt-5">
                <h1 className="fw-bold h2 pb-4 pb-md-4 mb-md-3">
                  What to expect during the Electronic Prescribing for Controlled Substances (EPCS)
                  process.
                </h1>
              </div>
              <div className="row row-cols-1 row-cols-sm-2 g-3 g-md-3 pb-2">
                <Card
                  icon={faEnvelopeOpenText}
                  title="Initial Onboarding"
                  header="STEP 1"
                >
                  Complete the registration form with accurate information. Upon submission, you
                  will receive an EPCS Invite email for Identity Proofing (IDP).
                </Card>
                <Card
                  icon={faUserShield}
                  title="Identity Proofing"
                  header="STEP 2"
                >
                  Complete the identity proofing process provided by DrFirst. The process requires
                  submitting identification documents, such as a driver's license, passport, or
                  other government-issued ID, along with a photo of yourself for verification.
                </Card>
                <Card
                  icon={faUserLock}
                  title="EPCS Registration"
                  header="STEP 3"
                >
                  Set up your two-factor authentication (2FA) credentials and establish a
                  passphrase. This passphrase will be required for authorizing controlled substance
                  prescriptions.
                  <br />
                  <br />
                  After receiving the verification code, continue the process by using the link
                  found in the original IDP success email from InfinID.
                  <br />
                  <br />
                  If it has been more than 7 business days since you completed the Identity Proofing
                  and have not received your IDP verification code,{' '}
                  <a
                    id="chat-with-us-anchor-instruction"
                    className="text-black text-purple"
                    href={hyperlinks['support_chat_url']}
                    target="_blank"
                  >
                    chat with us
                  </a>{' '}
                  or contact support at (855) 863-1355.
                </Card>
                <Card
                  icon={faUserFriends}
                  title="Logical Access Control (LAC)"
                  header="STEP 4"
                >
                  The DEA requires one additional individual to grant you "access control" to
                  electronically prescribe controlled substances. <br /> <br />
                  Invite a practice administrator or equivalent person to complete this step with
                  you.
                </Card>
              </div>

              {handleGetStarted && (
                <WorkflowNavigationButton
                  id="get-started-button-instruction"
                  text="Get Started"
                  variant="danger"
                  className="mt-3"
                  icon={faArrowRight}
                  iconRight
                  onClick={handleGetStarted}
                />
              )}
            </Container>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default ProviderInstruction;
